import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";

import {
  formatDay,
  formatProfile,
  formatTimeRange,
  sortDay,
} from "@/formatter";
import { CircularLoading } from "@/components/CircularLoading";
import { getOfficeDay } from "@/utils";

import trainerDefaultImage from "/assets/trainer-default-image.svg";

import type { Trainer } from "@/models";

type TrainerDetailCardProps = {
  data?: Trainer;
  isFetching: boolean;
};

export function TrainerDetailCard({
  data,
  isFetching,
}: TrainerDetailCardProps) {
  if (!data) return <></>;

  const {
    trainerProfileData: { avatarUrl, skills, workingDays, certificates },
    profile,
  } = data;

  const officeDay = getOfficeDay(workingDays).filter(({ enable }) => enable);
  const isHasSkill = skills.length > 0;

  return (
    <Card>
      {!isFetching ? (
        <CardContent sx={{ p: 0, pb: "0!important" }}>
          <Grid container p={{ xs: 0, sm: 3 }} spacing={{ xs: 0, sm: 3 }}>
            <Grid item xs={12} sm={4}>
              <Box width="100%" px={{ xs: "25%", sm: 0 }} py={{ xs: 3, sm: 0 }}>
                <Box
                  width="100%"
                  pt="150%"
                  borderRadius={2}
                  sx={{
                    backgroundImage: `url(${avatarUrl ?? trainerDefaultImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Stack px={{ xs: 2, sm: 0 }}>
                <Typography sx={{ typography: { xs: "subtitle1", sm: "h5" } }}>
                  {formatProfile(profile)}
                </Typography>
                {isHasSkill ? (
                  <Stack direction="row" mt={2} gap={2}>
                    <MilitaryTechIcon sx={{ color: "text.primary" }} />
                    <Stack>
                      {skills.map((skill) => (
                        <Typography
                          key={skill}
                          sx={{
                            typography: { xs: "subtitle2", sm: "subtitle1" },
                          }}
                        >
                          {skill}
                        </Typography>
                      ))}
                    </Stack>
                  </Stack>
                ) : (
                  <></>
                )}
                <Divider sx={{ my: { xs: 2, sm: 3 } }} />
                <Grid container spacing={{ xs: 2, md: 0 }}>
                  <Grid item xs={12} md={6}>
                    <Stack gap={{ xs: 0.5, sm: 1 }}>
                      <Typography
                        color="text.secondary"
                        sx={{
                          typography: { xs: "subtitle2", sm: "subtitle1" },
                        }}
                        mb={{ xs: 0.5, sm: 1 }}
                      >
                        วันและเวลาเข้างาน
                      </Typography>
                      {officeDay
                        .sort(({ day: d1 }, { day: d2 }) => sortDay(d1, d2))
                        .map((day) => (
                          <Stack direction="row" key={day.day} gap={3}>
                            <Typography
                              color="text.secondary"
                              variant="body2"
                              width="80px"
                            >
                              {formatDay(day.day)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {day.timeRange &&
                                formatTimeRange(
                                  day.timeRange.start,
                                  day.timeRange.end
                                )}
                            </Typography>
                          </Stack>
                        ))}
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6} mt={{ xs: 3, sm: 0 }} mb={2}>
                    <Stack gap={{ xs: 1, sm: 2 }}>
                      <Typography
                        color="text.secondary"
                        sx={{
                          typography: { xs: "subtitle2", sm: "subtitle1" },
                        }}
                      >
                        ใบรับรอง
                      </Typography>
                      {certificates.length
                        ? certificates.map((certificate, index) => (
                            <Typography
                              key={`${index}-${certificate}`}
                              color="text.secondary"
                              variant="body2"
                              display="list-item"
                              ml={2}
                            >
                              {certificate}
                            </Typography>
                          ))
                        : "-"}
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      ) : (
        <CircularLoading height={400} />
      )}
    </Card>
  );
}
